import { isNonNullish, isObjectType } from 'remeda'
import { hasArrayPath } from './object-path'
import type { ValidationPredicate } from './validators'

// Core stepper data types with strict readonly properties
type StepperValueItem = {
  readonly value: string
  readonly title?: string
  readonly label?: string
  readonly icon?: string
  readonly color?: string
}

type StepperCellData = {
  readonly _serverValue?: unknown
  readonly editorParams?: {
    readonly values?: readonly StepperValueItem[]
  }
  readonly value?: string | number
  readonly label?: string
  readonly editable?: boolean
}

// AG Grid parameter types for extraction functions
type GridParams = {
  readonly data: Record<string, unknown>
  readonly column: {
    readonly getColId: () => string
  }
}

/**
 * Type predicates for stepper data validation
 * Using functional composition for beautiful validation chains
 */

export const isValidStepperData: ValidationPredicate<StepperCellData> = (
  cellData
): cellData is StepperCellData =>
  hasArrayPath(cellData, ['editorParams', 'values'])

export const isValidStepperItem: ValidationPredicate<StepperValueItem> = (
  item
): item is StepperValueItem =>
  isObjectType(item) &&
  isNonNullish(item) &&
  'value' in item &&
  typeof item.value === 'string' &&
  item.value.length > 0

/**
 * Create text labels array for AG Grid rich select editor
 * Extracts display labels from stepper value items
 */
export const createTextValues = (cellData: unknown): readonly string[] => {
  // Extract values from cell data if valid stepper data
  if (!isValidStepperData(cellData)) {
    return []
  }

  const values = cellData.editorParams?.values || []

  // Map to display labels, filtering out invalid items
  return values
    .filter(isValidStepperItem)
    .map((item) => item.title || item.label || item.value)
    .filter((text) => text.length > 0)
}

/**
 * Generic utilities with proper type safety
 */

/** Parse JSON without asserting a trusted application type. */
export const parseJsonSafely = (json: string): unknown => {
  try {
    return JSON.parse(json) as unknown
  } catch (_error: unknown) {
    return undefined
  }
}

// Export types for external use
export type { GridParams, StepperCellData, StepperValueItem }
